// src/components/shared/Header.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Cache local para evitar el “flash” de logo genérico ---
function getCachedBranding() {
  try {
    return {
      b: localStorage.getItem("brand_dir") || null,
      c: localStorage.getItem("company_name") || null,
    };
  } catch {
    return { b: null, c: null };
  }
}
function setCachedBranding(brand_dir, company_name) {
  try {
    if (brand_dir) localStorage.setItem("brand_dir", brand_dir);
    if (company_name) localStorage.setItem("company_name", company_name);
  } catch {}
}

export default function Header({
  usuario, onLogout,
  title, subtitle,
  showBack = false, onBack,
  homePath = "/", onHome,
  children,
  showSwitchVista = false,
  switchLabel = "Vista empleado",
  switchToPath = "/empleado",
  onSwitch,

  // props explícitos para forzar branding
  brandDir: brandDirProp,
  companyName: companyNameProp,
}) {
  const navigate = useNavigate();
  const isLoggedIn = !!usuario;

  // Normaliza usuario a objeto
  const userObj = (typeof usuario === "object" && usuario) ? usuario : {};

  // Estado de rescate: arranca con lo que haya en cache → NO hay flash
  const cached = getCachedBranding();
  const [fallbackBrand, setFallbackBrand] = useState(cached.b);
  const [fallbackCompany, setFallbackCompany] = useState(cached.c);

  // Helpers token
  function getAccessTokenFromStorage() {
    try {
      const fromSession =
        sessionStorage.getItem("access") ||
        sessionStorage.getItem("access_token") ||
        sessionStorage.getItem("token");
      const fromLocal =
        localStorage.getItem("access") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token");
      return fromSession || fromLocal || null;
    } catch {
      return null;
    }
  }

  // Si llegan brand/company por props o usuario, guárdalos en cache
  useEffect(() => {
    if (brandDirProp) setCachedBranding(brandDirProp, null);
    if (companyNameProp) setCachedBranding(null, companyNameProp);
  }, [brandDirProp, companyNameProp]);

  useEffect(() => {
    if (userObj?.brand_dir) setCachedBranding(userObj.brand_dir, null);
    if (userObj?.company_name) setCachedBranding(null, userObj.company_name);
  }, [userObj?.brand_dir, userObj?.company_name]);

  // Rescate de branding desde /auth/me si aún falta
  useEffect(() => {
    let cancel = false;
    const needBrand = !(brandDirProp || userObj?.brand_dir || fallbackBrand);
    const needCompany = !(companyNameProp || userObj?.company_name || fallbackCompany);
    if (!needBrand && !needCompany) return;

    async function fetchUserForBranding() {
      try {
        const token = getAccessTokenFromStorage();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const candidates = ["/auth/me", "/me", "/api/auth/me", "/api/me"];

        for (const url of candidates) {
          try {
            const r = await fetch(url, { method: "GET", headers, credentials: token ? "same-origin" : "include" });
            if (!r.ok) continue;
            const d = await r.json();
            if (cancel || !d?.user) continue;

            if (!brandDirProp && !userObj?.brand_dir && d.user.brand_dir) {
              setFallbackBrand(d.user.brand_dir);
              setCachedBranding(d.user.brand_dir, null);
            }
            if (!companyNameProp && !userObj?.company_name && d.user.company_name) {
              setFallbackCompany(d.user.company_name);
              setCachedBranding(null, d.user.company_name);
            }
            break;
          } catch { /* probar siguiente */ }
        }
      } catch (err) {
        // noop
      }
    }
    fetchUserForBranding();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandDirProp, companyNameProp, userObj?.brand_dir, userObj?.company_name]);

  // Resolución final de branding/título
  const brandDir = brandDirProp || userObj?.brand_dir || fallbackBrand;
  const companyName = companyNameProp || userObj?.company_name || fallbackCompany;

  const logoSrc = useMemo(() => {
    return brandDir ? `/brands/${brandDir}/logo.png?v=hdr110` : `/logo.png?v=hdr110`;
  }, [brandDir]);

  const effectiveTitle = companyName || title || "Fichajes";
  const userLabel = typeof usuario === "string" ? usuario : (usuario?.email || "");

  const handleBack = () => (onBack ? onBack() : navigate(-1));
  const goProfile = () => navigate("/profile");
  const goHome = () => (onHome ? onHome() : navigate(homePath));
  const handleSwitch = () => {
    try { localStorage.setItem("lastView", switchToPath); } catch {}
    if (onSwitch) onSwitch(); else navigate(switchToPath);
  };
  const switchTarget = String(switchToPath || "").toLowerCase();
  const isAdminView =
    switchTarget.includes("empleado")
      ? true
      : switchTarget.includes("admin")
        ? false
        : String(switchLabel || "").toLowerCase().includes("empleado");
  const onKeyActivate = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goHome(); }
  };

  return (
    <header className="bg-white/90 backdrop-blur border-b shadow-sm">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-5 flex flex-wrap items-center justify-between gap-2">
        {/* IZQUIERDA */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {showBack && (
            <button
              onClick={handleBack}
              className="px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md bg-white hover:bg-slate-50 border border-slate-200 shadow-sm"
              title="Volver" aria-label="Volver"
            >
              ←
            </button>
          )}

          <div
            role="link" tabIndex={0}
            onClick={goHome} onKeyDown={onKeyActivate}
            className="group flex items-center gap-2 sm:gap-4 cursor-pointer select-none min-w-0"
            aria-label="Ir al panel de inicio" title="Ir al panel de inicio"
          >
            <img
              src={logoSrc}
              alt={`${effectiveTitle} :: ${brandDir || "no-brand"}`}
              data-build="hdr-110"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.png?v=hdr110"; }}
              className="w-[40px] h-[40px] sm:w-[72px] sm:h-[72px] object-contain transition-transform group-hover:scale-[1.02] flex-shrink-0"
            />
            <div className="flex flex-col min-w-0">
              <h1 className="text-base sm:text-2xl md:text-3xl font-semibold text-[#004B87] tracking-tight leading-tight truncate">
                {effectiveTitle}
              </h1>
              {subtitle && (
                <span className="hidden sm:block text-xs sm:text-sm text-slate-600 truncate">
                  {subtitle}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* DERECHA */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-wrap justify-end">
          {children}

          {showSwitchVista && (
            <button
              type="button"
              role="switch"
              aria-checked={isAdminView}
              onClick={handleSwitch}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50/80 px-2.5 sm:px-3.5 py-1.5 sm:py-2 transition-colors duration-200 ease-in-out hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004B87]/30 whitespace-nowrap"
              title={switchLabel} aria-label={switchLabel}
            >
              <span className={`text-xs sm:text-sm font-medium transition-colors ${isAdminView ? "text-slate-500" : "text-[#004B87]"}`}>
                Empleado
              </span>
              <span className="relative w-12 sm:w-14 h-6 sm:h-7 rounded-full bg-white border border-slate-200 flex items-center px-1 transition-all duration-200 ease-in-out">
                <span
                  className={`h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-[#004B87] transition-transform duration-200 ease-in-out ${
                    isAdminView ? "translate-x-6 sm:translate-x-7" : "translate-x-0"
                  }`}
                />
              </span>
              <span className={`text-xs sm:text-sm font-medium transition-colors ${isAdminView ? "text-[#004B87]" : "text-slate-500"}`}>
                Administrador
              </span>
            </button>
          )}

          {isLoggedIn ? (
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-sm sm:text-base text-gray-700">
              <div className="relative group cursor-default flex items-center gap-1.5 sm:gap-2 min-w-0" title={userLabel}>
                <span className="text-lg sm:text-xl">👤</span>
                <span className="hidden sm:inline font-medium max-w-[30vw] truncate">{userLabel}</span>
                <div className="absolute hidden group-hover:block sm:hidden bg-white border rounded shadow px-2 py-1 text-xs left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap z-10">
                  {userLabel}
                </div>
              </div>

              <button
                onClick={goProfile}
                className="px-2.5 sm:px-4 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 shadow-sm whitespace-nowrap flex items-center gap-2"
                title="Cambiar contraseña" aria-label="Cambiar contraseña"
              >
                <span className="sm:hidden">🔑</span>
                <span className="hidden sm:inline">Perfil</span>
              </button>

              {onLogout && (
                <button
                  onClick={onLogout}
                  className="bg-[#004B87] text-white px-2.5 sm:px-4 py-2 rounded-md hover:bg-[#003B6D] transition whitespace-nowrap flex items-center gap-2"
                  aria-label="Cerrar sesión" title="Cerrar sesión"
                >
                  <span className="sm:hidden">🚪</span>
                  <span className="hidden sm:inline">Cerrar sesión</span>
                </button>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-600 whitespace-nowrap">Inicia sesión para acceder</div>
          )}
        </div>
      </div>
    </header>
  );
}
